import dayjs, { Dayjs } from 'dayjs';

import { createSnapshot, duplicateSnapshot, updateSnapshot } from 'apis/SnapshotsAPI';
import { sortCommunityContracts } from 'components/CommunityInfo/careTypes/utils';
import { isMysteryShopAdmin } from 'helpers/permissions';
import { UserType } from 'store/slices/authSlice';
import { ApartmentTypeGroup } from 'types/accommodations';
import { CommunityContract, CommunityContractBenefit } from 'types/contracts';
import { SnapshotDataType } from 'types/snapshot';
import { convertToBase64 } from 'utils/helpers/files';
import { isNonNegativeNumber } from 'utils/helpers/numbers';
import { flattenObjects } from 'utils/helpers/object';

import { sortAccommodationEntriesByBaseRent } from './accommodations/helpers';
import { FIELD_CATEGORY, SHOP_DATA_TABS_GENERAL_KEY, VACANCY_TYPE } from './constants';
import {
  SHOP_DATA_DEFAULT_MENU_ITEM_INDEX,
  SHOP_DATA_MODAL_GENERAL_SIDEBAR_MENU_ITEMS,
  SHOP_DATA_MODAL_LIVING_TYPE_SIDEBAR_MENU_ITEMS,
} from './menus';
import {
  ShopDataModalSidebarMenuItem,
  SnapshotAccommodationType,
  SnapshotFeeIncentiveType,
  SnapshotFeeType,
  SnapshotOccupancyType,
  SnapshotOutboundDataType,
  SnapshotShopNoteType,
} from './types';

export const getSnapshotBoundOccupancies = (occupancies: any[] = [], snapshot: SnapshotDataType | undefined) => {
  if (!snapshot) {
    return occupancies;
  }

  return occupancies.filter((item) => {
    if (item?.snapshot_date && snapshot.date) {
      return dayjs(item.snapshot_date).isSame(dayjs(snapshot.date), 'day');
    }

    return false;
  });
};

export const getContractBenefitsMenuItems = (communityContracts: CommunityContract[]) => {
  if (!communityContracts) {
    return [];
  }
  const uniqueContractNames: string[] = Array.from(new Set(communityContracts.map((c) => c.name)));
  const uniqueContracts: CommunityContract[] = uniqueContractNames
    .map((contractName) => communityContracts.find((c) => c.name === contractName))
    .filter((c): c is CommunityContract => c !== undefined)
    .sort(sortCommunityContracts);

  return uniqueContracts.map((contract, index) => ({
    step: SHOP_DATA_MODAL_GENERAL_SIDEBAR_MENU_ITEMS.length + index,
    label: contract.name,
    key: contract.name,
    contract_id: contract.contract_id,
  }));
};

export const resolveTabStep = (
  currentTab: string,
  currentStep: number,
  increment: number,
  isCommunityCcrc: boolean,
  community: any
) => {
  const isGeneralTab = currentTab === SHOP_DATA_TABS_GENERAL_KEY;

  let generalTabMenuItems;
  if (isCommunityCcrc) {
    const dynamicItems: ShopDataModalSidebarMenuItem[] = getContractBenefitsMenuItems(community?.contracts || []);
    generalTabMenuItems = [...SHOP_DATA_MODAL_GENERAL_SIDEBAR_MENU_ITEMS, ...dynamicItems];
  } else {
    generalTabMenuItems = SHOP_DATA_MODAL_GENERAL_SIDEBAR_MENU_ITEMS;
  }

  const currentMenuItems = isGeneralTab ? generalTabMenuItems : SHOP_DATA_MODAL_LIVING_TYPE_SIDEBAR_MENU_ITEMS;

  const lastStepIndex = currentMenuItems.length - 1;
  const isFirstStep = currentStep === SHOP_DATA_DEFAULT_MENU_ITEM_INDEX;
  const isLastStep = currentStep === lastStepIndex;

  let tab = currentTab;
  const step = currentStep + increment;

  // Moving forward from last step
  if (isLastStep && increment > 0) {
    tab = String(Number(currentTab) + increment);
    return { tab, step: SHOP_DATA_DEFAULT_MENU_ITEM_INDEX };
  }

  // Moving backward from the first step
  if (isFirstStep && increment < 0) {
    const newTab = String(Number(currentTab) + increment);
    const newMenuItems =
      newTab === SHOP_DATA_TABS_GENERAL_KEY ? generalTabMenuItems : SHOP_DATA_MODAL_LIVING_TYPE_SIDEBAR_MENU_ITEMS;

    return { tab: newTab, step: newMenuItems.length - 1 };
  }

  // If within valid range, update step normally
  if (step >= 0 && step <= lastStepIndex) {
    return { tab, step };
  }

  // Otherwise, return current values
  return { tab: currentTab, step: currentStep };
};

const getNormalizedAccommodations = (
  accommodations: SnapshotOutboundDataType['accommodations'],
  apartmentTypeGroups: ApartmentTypeGroup[]
) => {
  const groupVariantIds = apartmentTypeGroups.reduce<Record<number, number[]>>((acc, group) => {
    acc[group.id] = [...(group.apartmentTypes ?? [])]
      .sort((first, second) => {
        const firstOrder = first.order ?? Number.MAX_SAFE_INTEGER;
        const secondOrder = second.order ?? Number.MAX_SAFE_INTEGER;

        if (firstOrder !== secondOrder) {
          return firstOrder - secondOrder;
        }

        return first.id - second.id;
      })
      .map((apartmentType) => apartmentType.id);

    return acc;
  }, {});

  return Object.values(accommodations)
    .filter((livingTypeGroup) => typeof livingTypeGroup === 'object' && livingTypeGroup !== null)
    .flatMap((livingTypeGroup) =>
      Object.entries(livingTypeGroup).flatMap(([contractsSlug, apartmentRoomTypeGroup]) =>
        Object.values(apartmentRoomTypeGroup)
          .filter((apartmentRoomType) => typeof apartmentRoomType === 'object' && apartmentRoomType !== null)
          .flatMap((apartmentRoomType) => {
            const entries = Object.entries(apartmentRoomType);
            const firstAccommodation = entries[0]?.[1];
            const variantsForGroup = groupVariantIds[firstAccommodation?.apartment_type_group_id ?? -1] ?? [];
            const sortedAccommodations = sortAccommodationEntriesByBaseRent(entries);

            return sortedAccommodations.map(([, accommodation], index) => ({
              ...accommodation,
              apartment_type_id: variantsForGroup[index] ?? accommodation.apartment_type_id,
              contracts_slug: contractsSlug,
            }));
          })
      )
    );
};

type SnapshotSavePayload =
  | SnapshotDataType
  | {
      id?: number;
      community: number;
      date: Dayjs;
      latest: boolean;
      draft: boolean;
      shop_request_id?: number;
      shared?: boolean;
    };

export const willDuplicateSnapshot = (snapshot: SnapshotSavePayload | undefined, currentUser: UserType | null) => {
  // Shared system snapshots are duplicated for assigned non-admin company users instead of updated in place.
  const nonAdminUser = !(currentUser?.isSuperuser || isMysteryShopAdmin(currentUser));
  const isAssignedUser = Boolean(currentUser?.company);

  return Boolean(snapshot?.id && snapshot.shared && !snapshot.draft && nonAdminUser && isAssignedUser);
};

export const createSnapshotData = async (
  snapshot: SnapshotSavePayload,
  data: SnapshotOutboundDataType,
  currentUser: UserType | null,
  apartmentTypeGroups: ApartmentTypeGroup[]
) => {
  const shouldDuplicateSnapshot = willDuplicateSnapshot(snapshot, currentUser);

  const normalizeIdValue = (value: any) => {
    if (value == null || value === '') {
      return undefined;
    }

    if (typeof value === 'object') {
      if ('id' in value && value.id != null) {
        return value.id;
      }

      return undefined;
    }

    return value;
  };

  const dataPayload: {
    [key: string]: any;
  } = {};

  const {
    accommodations,
    documents,
    feesIncentives,
    fees,
    occupancies,
    occupanciesOverall,
    shopNotes,
    contractBenefits,
  }: SnapshotOutboundDataType = data;

  try {
    try {
      // Accommodation
      const all_accommodations = getNormalizedAccommodations(accommodations, apartmentTypeGroups);

      dataPayload[FIELD_CATEGORY.ACCOMMODATIONS] = all_accommodations.map((item: SnapshotAccommodationType) => {
        const pricePerSquareFoot = item.base_rent / item.sq_footage;
        return {
          id: item?.id,
          living_type_id: item.living_type_id,
          apartment_type_id: item.apartment_type_id,
          unit_type: item.unit_type,
          base_rent: item.base_rent,
          entrance_fee: item.entrance_fee,
          sq_footage: item.sq_footage,
          price_per_sq: pricePerSquareFoot,
          notes: item.notes,
          contract_slugs: item.contracts_slug,
        };
      });
    } catch (e) {
      console.error('Trouble parsing Accommodation data in Snapshot payload!');
      throw e;
    }

    try {
      // Fees
      const allFees = Object.entries(feesIncentives ?? {})
        .flatMap(([livingTypeAbv, contractTypeFees]) => {
          return Object.entries(contractTypeFees ?? {}).map(([contractsSlug, feeData]) => {
            // Create a copy of the fee data and add contracts_slug if it doesn't exist
            return {
              ...feeData,
              contracts_slug: contractsSlug,
            };
          });
        })
        .filter(
          (item: SnapshotFeeIncentiveType) =>
            (isNonNegativeNumber(item.community_fee) && !item.enable_community_fee_equivalent) ||
            (isNonNegativeNumber(item.frequency_percentage) && item.enable_community_fee_equivalent) ||
            (item.fields ?? []).length > 0
        );

      dataPayload['fees_incentives'] = allFees.map((item: SnapshotFeeIncentiveType) => {
        const frequencyCalculated = item.enable_community_fee_equivalent
          ? (item.frequency_percentage ?? 0) / 100
          : undefined;

        return {
          id: item.id,
          living_type_id: item.living_type_id,
          community_fee: item.community_fee,
          frequency: frequencyCalculated,
          additional_fee: item.additional_fee,
          deposit: item.deposit,
          emergency_pendant_fee: item.emergency_pendant_fee,
          entrance_fee: item.entrance_fee,
          move_in_fee: item.move_in_fee,
          parking_fee: item.parking_fee,
          pet_fee: item.pet_fee,
          respite_fee: item.respite_fee,
          second_person_fee: item.second_person_fee,
          second_person_entrance_fee: item.second_person_entrance_fee,
          contract_slugs: item.contracts_slug,
        };
      });
    } catch (e) {
      console.error('Trouble parsing Fees data in Snapshot payload!');
      throw e;
    }

    try {
      // Level Fees
      const allFees = Object.entries(fees ?? {})
        .flatMap(([state, contracts]) =>
          Object.entries(contracts ?? {}).flatMap(([contractsSlug, feePricingTypes]) =>
            Object.values(feePricingTypes ?? {}).map((item: SnapshotFeeType) => ({
              ...item,
              contracts_slug: contractsSlug,
            }))
          )
        )
        .filter((item: SnapshotFeeType) => Boolean(item?.pricing_type_id && item?.fee_type));

      dataPayload[FIELD_CATEGORY.FEES] = allFees.map((item: SnapshotFeeType) => ({
        id: item.id,
        average_cost: item.average_cost,
        living_type_id: item.living_type_id,
        pricing_type_id: item.pricing_type_id,
        fee_type: item.fee_type,
        fee_note: item.fee_note,
        level_1: item.level_1,
        level_2: item.level_2,
        level_3: item.level_3,
        level_4: item.level_4,
        level_5: item.level_5,
        level_6: item.level_6,
        level_7: item.level_7,
        level_8: item.level_8,
        contract_slugs: item.contracts_slug,
      }));
    } catch (e) {
      console.error('Trouble parsing Fees data in Snapshot payload!');
      throw e;
    }

    try {
      // Occupancies
      const roomTypeOccupancies = flattenObjects(occupancies) || [];
      const overallOccupancies = flattenObjects(occupanciesOverall) || [];
      const mergedOccupancies = [...roomTypeOccupancies, ...overallOccupancies];

      dataPayload[FIELD_CATEGORY.OCCUPANCIES] = mergedOccupancies
        .filter((item: SnapshotOccupancyType) => Boolean(item.vacancy_type))
        .map((item: SnapshotOccupancyType) => {
          let total_apartments_units: number | null = null;
          let free_apartments_units: number | null = item.free_apartments_units;

          let pct_occupancy = item.pct_occupancy;

          if (item.vacancy_type === VACANCY_TYPE.UNIT) {
            total_apartments_units = (item.free_apartments_units ?? 0) + (item.occupied_apartments_units ?? 0);
            pct_occupancy =
              total_apartments_units > 0
                ? parseFloat(
                    (((total_apartments_units - item.free_apartments_units) / total_apartments_units) * 100).toFixed(2)
                  )
                : 0;
          } else if (item.vacancy_type === VACANCY_TYPE.PERCENTAGE) {
            free_apartments_units = null;
            total_apartments_units = null;
          }

          return {
            id: item.id,
            living_type_id: item.living_type_id,
            apartment_type_id: item.apartment_type_id,
            free_apartments_units,
            total_apartments_units,
            pct_occupancy,
          };
        });
    } catch (e) {
      console.error('Trouble parsing Occupancies data in Snapshot payload!');
      throw e;
    }

    try {
      // Shop Notes
      dataPayload['shop_notes'] = await Promise.all(
        shopNotes
          .filter((shopNote: SnapshotShopNoteType) => Boolean(shopNote?.request_notes) || shopNote?.attachment?.length)
          .map(async (shopNote: SnapshotShopNoteType) => {
            const hasAttachment = shopNote.attachment?.length > 0;
            const currentAttachment = hasAttachment ? shopNote.attachment[0] : undefined;
            const shouldUploadAttachment =
              hasAttachment && (!currentAttachment?.mock || shouldDuplicateSnapshot) && currentAttachment.originFileObj;
            const attachment = shouldUploadAttachment
              ? {
                  name: currentAttachment.name,
                  data: await convertToBase64(currentAttachment.originFileObj),
                }
              : null;

            const fileType = currentAttachment?.type;

            const shopNotePayload = Object.assign(
              {},
              { ...shopNote },
              {
                based_on: 'upload',
                mock: currentAttachment?.mock,
                attachment: attachment,
                type: fileType,
                attachment_mime_type: fileType,
                file_type: fileType,
                file_mime_type: fileType,
              }
            );

            return shopNotePayload;
          })
      );
    } catch (e) {
      console.error('Trouble parsing Shop Note data in Snapshot payload!');
      throw e;
    }

    try {
      // Documents
      const documentsMissingCategory = (documents ?? []).some(
        (file: any) => !file?.file_category_ids?.length && (file?.originFileObj || file?.id)
      );

      if (documentsMissingCategory) {
        throw new Error('Please select a category for each document before saving.');
      }

      const documentsPayload = await Promise.all(
        (documents ?? []).map(async (file: any) => {
          const shouldUploadFile = !file?.mock || shouldDuplicateSnapshot;
          const fileData = shouldUploadFile && file?.originFileObj ? await convertToBase64(file.originFileObj) : null;

          return {
            id: file.id,
            name: file.name ?? 'Untitled',
            mock: file.mock,
            file_category_ids: file.file_category_ids ?? [],
            analysis_run_id: normalizeIdValue(file.analysis_run_id),
            suggested_file_category_ids: file.suggested_file_category_ids ?? [],
            summary: file.summary ?? '',
            file: fileData
              ? {
                  name: file.name ?? 'Untitled',
                  data: fileData,
                }
              : null,
          };
        })
      );
      dataPayload[FIELD_CATEGORY.DOCUMENTS] = documentsPayload;
    } catch (e) {
      console.error('Trouble parsing Brochures and Documents Note data in Snapshot payload!');
      throw e;
    }

    try {
      // Contract Benefits
      const transformedBenefits = Object.values(contractBenefits).flatMap((benefitsById) =>
        Object.values(benefitsById).map((benefit) => ({
          ...benefit,
          community_id_writeable: benefit.community_id,
          contract_id_writeable: benefit.contract_id,
        }))
      );

      dataPayload['contract_benefits'] = transformedBenefits.filter((b) => b.benefit.length);
    } catch (e) {
      console.error('Trouble parsing Contract Benefits data in Snapshot payload!');
      throw e;
    }

    let response = undefined;
    if (shouldDuplicateSnapshot && snapshot.id) {
      response = await duplicateSnapshot(snapshot.id, { data: dataPayload });
    } else if (snapshot && snapshot.id) {
      response = await updateSnapshot(snapshot.id, { ...snapshot, data: dataPayload });
    } else {
      response = await createSnapshot({ ...snapshot, data: dataPayload });
    }

    return response;
  } catch (e) {
    console.error('Trouble preparing data for the Snapshot payload:', e);
    throw e;
  }
};

export const normalizeContractBenefits = (
  input: CommunityContractBenefit[] | Record<string, CommunityContractBenefit[]>
): Record<string, CommunityContractBenefit[]> => {
  if (!input) return {};

  if (!Array.isArray(input)) {
    return input;
  }

  return input.reduce(
    (acc, item) => {
      const category_id = item?.category_id;
      if (!category_id) return acc;
      if (!acc[category_id]) acc[category_id] = [];
      acc[category_id].push(item);
      return acc;
    },
    {} as Record<string, CommunityContractBenefit[]>
  );
};

export const flattenContractBenefits = (
  grouped: Record<string, CommunityContractBenefit[]> | undefined
): CommunityContractBenefit[] => {
  if (!grouped || typeof grouped !== 'object') return [];
  return Object.values(grouped).flat();
};
